import * as React from 'react';
import cn from 'classnames';

import { useTheme } from '../../../theme';
import { CdnIcon } from '@shared/ui';
import { WEEK_CALENDAR_DAY_INDEX_ATTR } from '../../constants';
import { useScrollCalendar } from '../../hooks/useScrollCalendar';
import { WeekCalendarSliderMode } from '../../types';

interface Props {
	mode?: WeekCalendarSliderMode;
	calendarDaysCount?: number;
	selectedIndex: number;
	dates: Date[];
	centralDate?: Date;
	loading?: boolean;
	hideArrows?: boolean;
	disabledLeftArrow?: boolean;
	disabledRightArrow?: boolean;
	minItemWidth?: number;
	onRangeChange?: (type: 'increase' | 'decrease', visibleDates: number) => void;
	renderDay: (date: Date) => React.ReactNode;
}

const Slider: React.FC<Props> = ({
	mode = 'scroll',
	calendarDaysCount,
	selectedIndex,
	dates,
	centralDate,
	loading,
	hideArrows,
	disabledLeftArrow,
	disabledRightArrow,
	minItemWidth,
	onRangeChange,
	renderDay
}) => {
	const css = useTheme('WeekCalendar').SliderStyles;

	const isStatic = mode === 'static';

	const { viewportRef, allDaysFit, navigate, onTouchStart, onTouchMove, onTouchEnd } = useScrollCalendar({
		mode,
		calendarDaysCount,
		selectedIndex,
		dates,
		centralDate,
		loading,
		disabledLeftArrow,
		disabledRightArrow,
		minItemWidth,
		onRangeChange
	});

	return (
		<div className={css.container}>
			{!hideArrows && (
				<>
					<div
						className={cn(css.arrow, css.arrow_prev, {
							[css.disabled]: disabledLeftArrow
						})}
						onClick={() => !disabledLeftArrow && navigate('prev')}
					>
						<CdnIcon icon="Arrow/Chevron_left" />
					</div>

					<div
						className={cn(css.arrow, css.arrow_next, {
							[css.disabled]: disabledRightArrow
						})}
						onClick={() => !disabledRightArrow && navigate('next')}
					>
						<CdnIcon icon="Arrow/Chevron_right" />
					</div>
				</>
			)}

			<div
				ref={viewportRef}
				className={cn(css.viewport, { [css.viewportStatic]: isStatic })}
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onTouchEnd={onTouchEnd}
			>
				<div className={cn(css.daysContainer, { [css.daysContainerFits]: allDaysFit })}>
					{dates.map((date, index) => (
						<div key={index} className={css.daySlot} {...{ [WEEK_CALENDAR_DAY_INDEX_ATTR]: index }}>
							{renderDay(date)}
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default Slider;
